import { service } from '@ember/service'
import Component from '@glimmer/component'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import type { LegendOptions } from 'highcharts'
import type { IntlService } from 'ember-intl'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import LeftSplitChart from 'teacher-ui-client/components/giraffe/left-split-chart'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SeriesColumn, {
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { BooksReadByGenreTotalsData } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre-totals'

interface Args {
  data: BooksReadByGenreTotalsData
  grade: string
  summaryTitle: string
  title: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class BooksReadByGenreTotals extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Spacing for the SeriesColumn chart
   */
  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  /**
   * Colour scheme for the SummaryBox
   */
  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.Pink

  /**
   * Chart legend.
   */
  readonly legend: LegendOptions = legend('left', 'top', 50, -15)

  /**
   * List of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories(): string[] {
    const months = this.args.data.months?.map((data) => data.month) ?? []
    return months.map((month) => convertDateFormat(month))
  }

  readonly colorMap = {
    fiction: colors.purplyPink400,
    nonfiction: colors.purplyPink300,
  }

  /**
   * List of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    const monthData = this.args.data.months ?? []

    return (['fiction', 'nonfiction'] as const).map((genre) => {
      const name = this.intl.t(`reporting.charts.booksRead.genres.${genre}`)
      const data = monthData.map((data) => data[genre])
      const color = this.colorMap[genre]

      return { name, data, color, type: 'column' }
    })
  }

  /**
   * List of summary statistics, extracted from the input data.
   * Display total books read and student counts, and average books per student.
   */
  get summaryStats(): Stat[] {
    const { total_count, student_count, student_average } = this.args.data.summary

    const totalLabel = this.intl.t('reporting.statBoxes.courseProgress.booksRead')
    const countLabel = this.intl.t('reporting.statBoxes.courseProgress.studentCount')
    const perLabel = this.intl.t('reporting.statBoxes.courseProgress.perStudentCount')

    return [
      { label: totalLabel, value: formatNumber(total_count, 1) },
      {
        label: countLabel,
        value: formatNumber(student_count, 1),
      },
      {
        label: perLabel,
        value: formatNumber(student_average, 1),
      },
    ]
  }

  <template>
    <div data-test-giraffe-books-read-by-genre-totals class="relative h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <ChartInfoTooltip @tooltipContent={{@tooltip}} />
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisReversedStacks={{false}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart></div>
  </template>
}

export default BooksReadByGenreTotals
